import React from 'react';
import { Dialog } from 'primereact/dialog';
import { backend_url } from '../../../utils/urls';

const HandoverDetailsDialog = ({ visible, onHide, handover, loading }) => {
  if (!handover && !loading) return null;

  const facilityLogo = localStorage.getItem('selectedFacilityLogo') || '';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const defaultCurrencyCode =
    handover?.defaultCurrency?.currencyShortCode ||
    handover?.defaultCurrency?.code ||
    'USD';

  const formatMoney = (amount, currencyCode = defaultCurrencyCode) => {
    const numeric = parseFloat(amount || 0);
    return `${currencyCode} ${numeric.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getUnitName = () => {
    if (!handover?.unitId) return 'Unknown Unit';
    if (typeof handover.unitId === 'object' && handover.unitId !== null) {
      return handover.unitId.unitNumber || handover.unitId.name || 'Unknown Unit';
    }
    return handover.unitId;
  };

  const getCustomerName = () => {
    if (!handover?.customerId) return 'Unknown Customer';
    if (typeof handover.customerId === 'object' && handover.customerId !== null) {
      return (
        handover.customerId.name ||
        `${handover.customerId.firstName || ''} ${handover.customerId.lastName || ''}`.trim() ||
        'Unknown Customer'
      );
    }
    return handover.customerId;
  };

  const customerEmail =
    typeof handover?.customerId === 'object' ? handover.customerId.email : 'N/A';
  const customerPhone =
    typeof handover?.customerId === 'object' ? handover.customerId.phoneNumber : 'N/A';
  const customerIdNumber =
    typeof handover?.customerId === 'object' ? handover.customerId.idNumber : 'N/A';
  const propertyManagerName =
    typeof handover?.propertyManagerId === 'object' ? handover.propertyManagerId.name : 'N/A';
  const propertyManagerEmail =
    typeof handover?.propertyManagerId === 'object' ? handover.propertyManagerId.email : 'N/A';

  const depositAmount = handover?.securityDeposit?.amount || 0;
  const deductions = Array.isArray(handover?.securityDeposit?.deductions)
    ? handover.securityDeposit.deductions
    : [];
  const totalDeductions = deductions.reduce(
    (sum, deduction) => sum + (parseFloat(deduction.amount) || 0),
    0
  );
  const refundAmount =
    handover?.securityDeposit?.refundAmount != null
      ? handover.securityDeposit.refundAmount
      : depositAmount - totalDeductions;

  return (
    <Dialog
      header={`${handover?.handoverType === 'MoveIn' ? 'Move-In' : 'Move-Out'} Handover Details`}
      visible={visible}
      style={{ width: '80vw' }}
      modal
      onHide={onHide}
    >
      <div className="p-3">
        <div className="d-flex align-items-center mb-4">
          {facilityLogo && (
            <img
              src={facilityLogo}
              alt="Company Logo"
              style={{ width: '60px', height: '60px', objectFit: 'contain' }}
              className="me-3"
            />
          )}
          <div>
            <h4 className="mb-1">
              {handover?.handoverType === 'MoveIn' ? 'Move-In' : 'Move-Out'} Handover
            </h4>
            <small className="text-muted">Detailed handover summary</small>
          </div>
        </div>

        {loading && (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="text-center text-muted">
              <i className="ti ti-loader" style={{ fontSize: '2rem' }}></i>
              <div className="mt-2">Loading handover details...</div>
            </div>
          </div>
        )}

        {!loading && (
          <>
            {/* Basic Information */}
            <div className="row mb-4">
              <div className="col-md-6">
                <h5>Basic Information</h5>
                <div className="row mb-2">
                  <div className="col-md-4 fw-bold">Unit:</div>
                  <div className="col-md-8">{getUnitName()}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-4 fw-bold">Customer:</div>
                  <div className="col-md-8">{getCustomerName()}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-4 fw-bold">Handover Date:</div>
                  <div className="col-md-8">{formatDate(handover.handoverDate)}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-4 fw-bold">Status:</div>
                  <div className="col-md-8">
                    <span
                      className={`badge ${handover.status === 'Completed' ? 'bg-success' : 'bg-warning'}`}
                    >
                      {handover.status || 'Draft'}
                    </span>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-4 fw-bold">Keys Handed Over:</div>
                  <div className="col-md-8">{handover.keysHandedOver || 0}</div>
                </div>
              </div>

              {/* Meter Readings */}
              <div className="col-md-6">
                <h5>Meter Readings</h5>
                {handover.meterReadings ? (
                  <>
                    {handover.meterReadings.electricity && (
                      <div className="row mb-2">
                        <div className="col-md-4 fw-bold">Electricity:</div>
                        <div className="col-md-8">
                          {handover.meterReadings.electricity.reading || 'Not recorded'}
                          {handover.meterReadings.electricity.imageUrl && (
                            <div className="mt-1">
                              <img
                                src={handover.meterReadings.electricity.imageUrl}
                                alt="Electricity Meter"
                                style={{ maxWidth: '100px', maxHeight: '100px' }}
                                className="rounded"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {handover.meterReadings.water && (
                      <div className="row mb-2">
                        <div className="col-md-4 fw-bold">Water:</div>
                        <div className="col-md-8">
                          {handover.meterReadings.water.reading || 'Not recorded'}
                          {handover.meterReadings.water.imageUrl && (
                            <div className="mt-1">
                              <img
                                src={handover.meterReadings.water.imageUrl}
                                alt="Water Meter"
                                style={{ maxWidth: '100px', maxHeight: '100px' }}
                                className="rounded"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {handover.meterReadings.gas && (
                      <div className="row mb-2">
                        <div className="col-md-4 fw-bold">Gas:</div>
                        <div className="col-md-8">
                          {handover.meterReadings.gas.reading || 'Not recorded'}
                          {handover.meterReadings.gas.imageUrl && (
                            <div className="mt-1">
                              <img
                                src={handover.meterReadings.gas.imageUrl}
                                alt="Gas Meter"
                                style={{ maxWidth: '100px', maxHeight: '100px' }}
                                className="rounded"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p>No meter readings recorded</p>
                )}
              </div>
            </div>

            {/* Customer & Property Manager Details */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h6 className="text-uppercase text-muted mb-3">Customer Details</h6>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">Name:</div>
                      <div className="col-8">{getCustomerName()}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">ID Number:</div>
                      <div className="col-8">{customerIdNumber || 'N/A'}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">Phone:</div>
                      <div className="col-8">{customerPhone || 'N/A'}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">Email:</div>
                      <div className="col-8">{customerEmail || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h6 className="text-uppercase text-muted mb-3">Property Manager</h6>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">Name:</div>
                      <div className="col-8">{propertyManagerName || 'N/A'}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4 fw-bold">Email:</div>
                      <div className="col-8">{propertyManagerEmail || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Deposit & Deductions */}
            {handover.handoverType === 'MoveOut' && (
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h6 className="text-uppercase text-muted mb-3">Security Deposit</h6>
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <div className="text-muted small">Deposit Amount</div>
                          <div className="fw-bold">{formatMoney(depositAmount)}</div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-muted small">Total Deductions</div>
                          <div className="fw-bold text-danger">{formatMoney(totalDeductions)}</div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-muted small">Refund Amount</div>
                          <div className="fw-bold text-success">{formatMoney(refundAmount)}</div>
                        </div>
                      </div>
                      <h6 className="text-uppercase text-muted mb-2">Deductions</h6>
                      {deductions.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Reason</th>
                                <th>Description</th>
                                <th className="text-end">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                          {deductions.map((deduction, index) => {
                            const deductionCurrency =
                              deduction.currency?.currencyShortCode || defaultCurrencyCode;
                            return (
                            <tr key={index}>
                              <td>{deduction.reason || 'Unspecified'}</td>
                              <td className="text-muted">{deduction.description || '-'}</td>
                              <td className="text-end">{formatMoney(deduction.amount, deductionCurrency)}</td>
                            </tr>
                          );})}
                        </tbody>
                      </table>
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No deductions recorded</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Inventory Items */}
            <div className="mt-4">
              <h5>Inventory Items</h5>
              {handover.items && handover.items.length > 0 ? (
                <div className="row">
                  {handover.items.map((item, index) => (
                    <div key={index} className="col-md-6 mb-3">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="card-title mb-2">
                            {item.name || 'Unnamed Item'}
                            <span
                              className={`badge ms-2 ${
                                item.condition === 'Excellent' || item.condition === 'Good'
                                  ? 'bg-success'
                                  : item.condition === 'Fair'
                                    ? 'bg-warning'
                                    : 'bg-danger'
                              }`}
                            >
                              {item.condition || 'Unknown'}
                            </span>
                          </h6>
                          <div className="mb-2">
                            <small className="text-muted">
                              <strong>Category:</strong> {item.category || 'Uncategorized'} |{' '}
                              <strong> Quantity:</strong> {item.quantity || 1}
                            </small>
                          </div>
                          {item.notes && <p className="text-muted small mb-2">{item.notes}</p>}
                          {item.images && item.images.length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted d-block mb-2">
                                <strong>Images ({item.images.length}):</strong>
                              </small>
                              <div className="row g-2">
                                {item.images.map((imageUrl, imgIndex) => (
                                  <div key={imgIndex} className="col-6">
                                    <img
                                      src={`${backend_url}${imageUrl}`}
                                      alt={`${item.name} - Image ${imgIndex + 1}`}
                                      style={{ width: '100%', height: '120px', objectFit: 'cover', cursor: 'pointer' }}
                                      className="rounded border"
                                      onClick={() => window.open(`${backend_url}${imageUrl}`, '_blank')}
                                      title="Click to view full size"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No items recorded</p>
              )}
            </div>

            {/* Unpaid Invoices */}
            <div className="mt-4">
              <h5>Unpaid Invoices</h5>
              {handover.unpaidInvoices && handover.unpaidInvoices.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Type</th>
                        <th>Due Date</th>
                        <th>Status</th>
                        <th className="text-end">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {handover.unpaidInvoices.map((invoice) => {
                        const invoiceCurrency =
                          invoice.currency?.code ||
                          invoice.currency?.currencyShortCode ||
                          defaultCurrencyCode;
                        return (
                          <tr key={invoice._id}>
                            <td>{invoice.invoiceNumber || 'N/A'}</td>
                            <td>{invoice.whatFor?.invoiceType || 'Invoice'}</td>
                            <td>{invoice.formattedDueDate || formatDate(invoice.dueDate)}</td>
                            <td>
                              <span className="badge bg-warning">{invoice.status || 'Unpaid'}</span>
                            </td>
                            <td className="text-end">{formatMoney(invoice.balance || 0, invoiceCurrency)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No unpaid invoices</p>
              )}
            </div>

            {/* Notes */}
            <div className="mt-4">
              <h5>Notes</h5>
              <p>{handover.notes || 'No additional notes'}</p>
            </div>

            {/* Attachments */}
            <div className="mt-4">
              <h5>Attachments</h5>
              {handover.attachments && handover.attachments.length > 0 ? (
                <div className="row">
                  {handover.attachments.map((attachment, index) => {
                    const fileToCheck = attachment.fileName || attachment.fileUrl || '';
                    const hasImageExtension = fileToCheck.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                    const isPdf = fileToCheck.toLowerCase().endsWith('.pdf');
                    const shouldShowAsImage =
                      hasImageExtension || (!isPdf && !fileToCheck.match(/\.(pdf|doc|docx|xls|xlsx|txt)$/i));

                    return (
                      <div key={index} className="col-md-6 mb-3">
                        <div className="card h-100">
                          <div className="card-body">
                            <h6 className="mb-2">
                              <i
                                className={`${
                                  isPdf ? 'ti ti-file-type-pdf text-danger' : 'ti ti-photo text-primary'
                                } me-2`}
                              ></i>
                              {attachment.name || attachment.fileName || `Attachment ${index + 1}`}
                            </h6>
                            {attachment.fileName && attachment.name !== attachment.fileName && (
                              <small className="text-muted d-block mb-2">File: {attachment.fileName}</small>
                            )}
                            {!attachment.name && attachment.fileName && (
                              <small className="text-warning d-block mb-2">
                                <i className="ti ti-alert-circle me-1"></i>
                                No custom name provided
                              </small>
                            )}
                            {attachment.uploadDate && (
                              <small className="text-muted d-block mb-2">
                                Uploaded: {formatDate(attachment.uploadDate)}
                              </small>
                            )}
                            {attachment.fileUrl && (
                              <div className="mt-2">
                                {shouldShowAsImage ? (
                                  <img
                                    src={attachment.fileUrl}
                                    alt={attachment.name || attachment.fileName}
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      maxHeight: '300px',
                                      objectFit: 'contain',
                                      cursor: 'pointer'
                                    }}
                                    className="rounded border"
                                    onClick={() => window.open(attachment.fileUrl, '_blank')}
                                    title="Click to view full size"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const link = document.createElement('a');
                                      link.href = attachment.fileUrl;
                                      link.target = '_blank';
                                      link.className = 'btn btn-sm btn-primary';
                                      link.innerHTML = '<i class="ti ti-download me-1"></i>Download File';
                                      e.target.parentElement.appendChild(link);
                                    }}
                                  />
                                ) : isPdf ? (
                                  <a
                                    href={attachment.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-danger"
                                  >
                                    <i className="ti ti-file-type-pdf me-1"></i>
                                    View PDF
                                  </a>
                                ) : (
                                  <a
                                    href={attachment.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-primary"
                                  >
                                    <i className="ti ti-download me-1"></i>
                                    Download File
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>No attachments</p>
              )}
            </div>

            {/* Signatures */}
            <div className="mt-4">
              <h5>Signatures</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="border rounded p-3">
                    <h6 className="mb-3">
                      <i className="ti ti-user-shield me-2 text-primary"></i>
                      Property Manager
                    </h6>
                    {handover.signatures?.propertyManager?.signature ? (
                      <div>
                        <div
                          className="bg-light p-2 rounded border mb-2"
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <img
                            src={handover.signatures.propertyManager.signature}
                            alt="Property Manager Signature"
                            style={{ maxWidth: '100%', maxHeight: '150px', height: 'auto' }}
                          />
                        </div>
                        <small className="text-muted">
                          <i className="ti ti-calendar me-1"></i>
                          Signed on:{' '}
                          {handover.signatures.propertyManager.date
                            ? formatDate(handover.signatures.propertyManager.date)
                            : 'N/A'}
                        </small>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-3">
                        <i className="ti ti-signature" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                        <p className="mb-0 mt-2">Not signed</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="border rounded p-3">
                    <h6 className="mb-3">
                      <i className="ti ti-user me-2 text-primary"></i>
                      Customer
                    </h6>
                    {handover.signatures?.customer?.signature ? (
                      <div>
                        <div
                          className="bg-light p-2 rounded border mb-2"
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <img
                            src={handover.signatures.customer.signature}
                            alt="Customer Signature"
                            style={{ maxWidth: '100%', maxHeight: '150px', height: 'auto' }}
                          />
                        </div>
                        <small className="text-muted">
                          <i className="ti ti-calendar me-1"></i>
                          Signed on:{' '}
                          {handover.signatures.customer.date
                            ? formatDate(handover.signatures.customer.date)
                            : 'N/A'}
                        </small>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-3">
                        <i className="ti ti-signature" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                        <p className="mb-0 mt-2">Not signed</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default HandoverDetailsDialog;
